<template>
  <div ref="componentContainer" class="row f-row mt-1 h-100">
    <!-- Table -->
    <div class="col col-12 bdr-r" :style="{ 'max-height': tableHeight + 'px' }">
      <div v-if="showLoading1" class="col col-12">
        <SkeletonSingleCommodity :nRows="3" :showFields="showFields" />
      </div>
      <div
        v-else-if="!showLoading1 && !showLoading2"
        class="col col-12 border_bottom"
      >
        <!-- {{ animateUp }}, {{ animateDown }} -->
        <DataTable
          columnResizeMode="expand"
          class="p-datatable-sm overview_table"
          resizableColumns
          scrollable
          tableStyle="table-layout: fixed; width: 100%;"
          style="width: 100%; table-layout: fixed;"
          :key="showFields ? 'full' : 'compact'"
          :scrollHeight="Math.max(tableHeight, 200) + 'px'"
          :value="spreadTable0data"
        >
          <!-- First commodity -->
          <Column
            field="contract_month_year"
            header="Month/Year"
            sortable
            frozen
          >
            <template #body="{ data }">
              <div
                class="d-flex align-items-center h-100 w-100"
                :class="{ ovPadding: TAVisible == -1 }"
              >
                <ButtonView
                  v-if="TAVisible > -1"
                  fontSize="12"
                  :showBorder="false"
                  @btnAbort="
                    () =>
                      onUserShowTAChart(data.continuous_contract, data.asset)
                  "
                />
                {{ data.contract_month_year }}
              </div>
            </template>
          </Column>

          <Column field="close" sortable>
            <template #header> Last {{ formatSpreadName(3, 0, 2) }} </template>
            <template #body="{ data, index }">
              <div
                class="d-flex align-items-center h-100 w-100 ovHighlight ovPadding"
                :class="{
                  row_up: animateUp.includes(index),
                  row_down: animateDown.includes(index)
                }"
              >
                <span
                  :class="arrowClass(data.change)"
                  v-if="data.change !== 0"
                ></span>
                <span class="ml-1"> {{ priceFormat(data.close) }}</span>
              </div>
            </template>
          </Column>

          <Column field="pct_change" header="% Chg" sortable>
            <template #body="{ data, index }">
              <div
                class="d-flex align-items-center h-100 w-100 ovPadding"
                :class="{
                  row_up: animateUp.includes(index),
                  row_down: animateDown.includes(index)
                }"
              >
                <span
                  :class="{ dayUp: data.change > 0, dayDown: data.change < 0 }"
                >
                  {{ percentageFormat(data.pct_change) }}
                </span>
              </div>
            </template>
          </Column>

          <Column
            v-if="showConverted && tableConversion != 0"
            field="close"
            sortable
          >
            <template #header>
              Converted {{ formatSpreadName(3, 1, 3) }}
            </template>
            <template #body="{ data, index }">
              <div
                class="d-flex align-items-center h-100 w-100 ovHighlight ovPadding"
                :class="{
                  row_up: animateUp.includes(index),
                  row_down: animateDown.includes(index)
                }"
              >
                <span
                  :class="arrowClass(data.change)"
                  v-if="data.change !== 0"
                ></span>
                <span class="ml-1">
                  {{
                    priceFormat(data.close / tableConversion / tableRate)
                  }}</span
                >
              </div>
            </template>
          </Column>

          <Column v-if="showFields" field="high" header="High">
            <template #body="{ data }">
              <div class="d-flex align-items-center h-100 w-100 ovPadding">
                {{ priceFormat(data.high) }}
              </div>
            </template>
          </Column>

          <Column v-if="showFields" field="low" header="Low">
            <template #body="{ data }">
              <div class="d-flex align-items-center h-100 w-100 ovPadding">
                {{ priceFormat(data.low) }}
              </div>
            </template>
          </Column>

          <Column v-if="showFields" field="observation_date" header="Updated">
            <template #body="{ data }">
              <div class="d-flex align-items-center h-100 w-100 ovPadding">
                {{ dateFormat(data.observation_date) }}
              </div>
            </template>
          </Column>

          <!-- Second commodity -->
          <Column
            :bodyStyle="{ borderLeft: '1px solid var(--lighterbackground)' }"
            field="contract_month_year"
            header="Month/Year"
            sortable
          >
            <template #body="{ _, index }">
              <div class="d-flex align-items-center h-100 w-100 ovPadding">
                <ButtonView
                  v-if="TAVisible > -1"
                  fontSize="12"
                  :showBorder="false"
                  @btnAbort="
                    () =>
                      onUserShowTAChart(
                        spreadTable1data[index].continuous_contract,
                        spreadTable1data[index].asset
                      )
                  "
                />
                {{ spreadTable1data[index].contract_month_year }}
              </div>
            </template>
          </Column>

          <Column field="close" sortable>
            <template #header> Last {{ formatSpreadName(3, 1, 3) }} </template>
            <template #body="{ _, index }">
              <div
                class="d-flex align-items-center h-100 w-100 ovHighlight ovPadding"
                :class="{
                  row_up: animateUp.includes(index + table1Len),
                  row_down: animateDown.includes(index + table1Len)
                }"
              >
                <span
                  :class="arrowClass(spreadTable1data[index].change)"
                  v-if="spreadTable1data[index].change !== 0"
                ></span>
                <span class="ml-1">
                  {{ priceFormat(spreadTable1data[index].close) }}</span
                >
              </div>
            </template>
          </Column>

          <Column field="pct_change" header="% Chg" sortable>
            <template #body="{ _, index }">
              <div
                class="d-flex align-items-center h-100 w-100 ovPadding"
                :class="{
                  row_up: animateUp.includes(index + table1Len),
                  row_down: animateDown.includes(index + table1Len)
                }"
              >
                <span
                  :class="{
                    dayUp: spreadTable1data[index].change > 0,
                    dayDown: spreadTable1data[index].change < 0
                  }"
                >
                  {{ percentageFormat(spreadTable1data[index].pct_change) }}
                </span>
              </div>
            </template>
          </Column>

          <Column
            v-if="showConverted && tableConversion != 0"
            field="close"
            sortable
          >
            <template #header>
              Converted {{ formatSpreadName(3, 0, 2) }}
            </template>
            <template #body="{ data, index }">
              <div
                class="d-flex align-items-center h-100 w-100 ovHighlight ovPadding"
                :class="{
                  row_up: animateUp.includes(index + table1Len),
                  row_down: animateDown.includes(index + table1Len)
                }"
              >
                <span
                  :class="arrowClass(spreadTable1data[index].change)"
                  v-if="spreadTable1data[index].change !== 0"
                ></span>
                <span class="ml-1">
                  {{
                    priceFormat(
                      spreadTable1data[index].close *
                        tableConversion *
                        tableRate
                    )
                  }}</span
                >
              </div>
            </template>
          </Column>

          <Column v-if="showFields" field="high" header="High">
            <template #body="{ _, index }">
              <div class="d-flex align-items-center h-100 w-100 ovPadding">
                {{ priceFormat(spreadTable1data[index].high) }}
              </div>
            </template>
          </Column>

          <Column v-if="showFields" field="low" header="Low">
            <template #body="{ _, index }">
              <div class="d-flex align-items-center h-100 w-100 ovPadding">
                {{ priceFormat(spreadTable1data[index].low) }}
              </div>
            </template>
          </Column>

          <Column v-if="showFields" field="observation_date" header="Updated">
            <template #body="{ _, index }">
              <div class="d-flex align-items-center h-100 w-100 ovPadding">
                {{ dateFormat(spreadTable1data[index].observation_date) }}
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>

    <div class="col col-12 h-100" :style="{ 'max-height': tableHeight + 'px' }">
      <div v-if="showLoading3">
        <SkeletonSwitch />
      </div>
      <div
        v-else
        class="col col-12"
        :style="{ 'max-height': tableHeight + 'px' }"
      >
        <DataTable
          columnResizeMode="expand"
          class="p-datatable-sm overview_table"
          resizableColumns
          scrollable
          tableStyle="table-layout: fixed; width: 100%;"
          :key="showFields ? 'full' : 'compact'"
          :scrollHeight="tableHeight + 'px'"
          :value="spreadTable[2].data"
        >
          <ColumnGroup type="header">
            <Row>
              <Column :colspan="2">
                <template #header>
                  <span class="ml-2 mr-1 ovHighlight">
                    {{ formatCommodity1 }}
                  </span>
                  switch
                </template>
              </Column>
              <Column :colspan="showConverted && tableConversion != 0 ? 3 : 2">
                <template #header>
                  <span class="ml-2 mr-1">Arbitrage</span>
                  <span v-if="tableRate != 1 && tableConversion != 0">
                    ({{ formatCurrencyName }} =
                    {{ tableRate | priceFormat }})</span
                  >
                  <span
                    v-else-if="tableConversion == 0"
                    class="warning-pill"
                    style="padding: 0 12px 0 12px"
                  >
                    Conversion not found for {{ formatCommodity2 }}</span
                  >
                </template>
              </Column>
              <Column :colspan="2">
                <template #header>
                  <span class="ml-2 mr-1 ovHighlight">{{
                    formatCommodity2
                  }}</span
                  >switch
                </template>
              </Column>
            </Row>
            <Row>
              <Column header="Switch" field="switch1" sortable />
              <Column field="close1" sortable>
                <template #header>
                  Last {{ formatSpreadName(3, 0, 2) }}
                </template>
              </Column>
              <Column
                header="Month/Year"
                field="contract_month_year"
                sortable
              />
              <Column field="arb1" sortable>
                <template #header>
                  {{ formatSpreadName(3, 0, 2) }}
                </template>
              </Column>
              <Column
                v-if="showConverted && tableConversion != 0"
                field="arb2"
                sortable
              >
                <template #header>
                  {{ formatSpreadName(3, 1, 3) }}
                </template>
              </Column>
              <Column header="Switch" field="switch2" sortable />
              <Column field="close2" sortable>
                <template #header>
                  Last {{ formatSpreadName(3, 1, 3) }}
                </template>
              </Column>
            </Row>
          </ColumnGroup>

          <!-- Switch 1 -->
          <Column field="switch1" header="Switch" sortable>
            <template #body="{ data }">
              <div class="d-flex align-items-center h-100 w-100 ovPadding">
                {{ data.switch1 }}
              </div>
            </template>
          </Column>

          <Column field="close1" sortable>
            <template #header>Last {{ formatSpreadName(3, 0, 2) }}</template>
            <template #body="{ data, index }">
              <div
                class="d-flex align-items-center h-100 w-100 ovHighlight ovPadding"
                :class="{
                  row_up: animateUp.includes(index - 1),
                  row_down: animateDown.includes(index - 1)
                }"
              >
                <span
                  :class="arrowClass(data.close1)"
                  v-if="data.close1 !== 0"
                ></span>
                <span class="ml-1"> {{ priceFormat(data.close1) }}</span>
              </div>
            </template>
          </Column>

          <!-- Arbitrage 1 -->
          <Column
            :bodyStyle="{ borderLeft: '1px solid var(--lighterbackground)' }"
            field="contract_month_year"
            header="Month/Year"
            sortable
          >
            <template #body="{ data }">
              <div class="d-flex align-items-center h-100 w-100 ovPadding">
                {{ data.contract_month_year }}
              </div>
            </template>
          </Column>

          <Column field="arb1" sortable>
            <template #header> {{ formatSpreadName(3, 0, 2) }}</template>
            <template #body="{ data, index }">
              <div
                class="d-flex align-items-center h-100 w-100 ovPadding"
                :class="{
                  row_up:
                    animateUp.includes(index) ||
                    animateUp.includes(index + table1Len),
                  row_down:
                    animateDown.includes(index) ||
                    animateUp.includes(index + table1Len)
                }"
              >
                <span :class="{ dayUp: data.arb1 > 0, dayDown: data.arb1 < 0 }">
                  {{ priceFormat(data.arb1) }}
                </span>
              </div>
            </template>
          </Column>

          <Column
            v-if="showConverted && tableConversion != 0"
            field="arb2"
            sortable
          >
            <template #header> {{ formatSpreadName(3, 1, 3) }}</template>
            <template #body="{ data, index }">
              <div
                class="d-flex align-items-center h-100 w-100 ovPadding"
                :class="{
                  row_up:
                    animateUp.includes(index) ||
                    animateUp.includes(index + table1Len),
                  row_down:
                    animateDown.includes(index) ||
                    animateUp.includes(index + table1Len)
                }"
              >
                <span :class="{ dayUp: data.arb2 > 0, dayDown: data.arb2 < 0 }">
                  {{ priceFormat(data.arb2) }}
                </span>
              </div>
            </template>
          </Column>

          <!-- Switch 2 -->
          <Column
            :bodyStyle="{ borderLeft: '1px solid var(--lighterbackground)' }"
            field="switch2"
            header="Switch"
            sortable
          >
            <template #body="{ data }">
              <div class="d-flex align-items-center h-100 w-100 ovPadding">
                {{ data.switch2 }}
              </div>
            </template>
          </Column>

          <Column field="close2" sortable>
            <template #header>Last {{ formatSpreadName(3, 1, 3) }}</template>
            <template #body="{ data, index }">
              <div
                class="d-flex align-items-center h-100 w-100 ovHighlight ovPadding"
                :class="{
                  row_up: animateUp.includes(index - 1 + table1Len),
                  row_down: animateDown.includes(index - 1 + table1Len)
                }"
              >
                <span
                  :class="arrowClass(data.close2)"
                  v-if="data.close2 !== 0"
                ></span>
                <span class="ml-1"> {{ priceFormat(data.close2) }}</span>
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script>
import ButtonView from "@/components/buttons/ButtonView.vue";
import Column from "primevue/column";
import ColumnGroup from "primevue/columngroup";
import DataTable from "primevue/datatable";
import Row from "primevue/row";
import SkeletonSingleCommodity from "@/components/overview/SkeletonSingleCommodity";
import SkeletonSwitch from "@/components/overview/SkeletonSwitch";
import { format, timeFormat } from "d3";

export default {
  name: "SpreadTable",
  props: {
    animateUp: { required: true, default: () => [] },
    animateDown: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    componentNumber: { required: true, default: 1 },
    showFields: { required: true, type: Boolean, default: false },
    spreadTable: { required: true, default: () => [] },
    TAVisible: { required: false, type: Number, default: -1 }
  },
  components: {
    ButtonView,
    Column,
    ColumnGroup,
    DataTable,
    Row,
    SkeletonSingleCommodity,
    SkeletonSwitch
  },
  data() {
    return {
      containerHeight: 200
    };
  },
  mounted() {
    this.updateHeight();
    window.addEventListener("resize", this.updateHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateHeight);
  },
  computed: {
    tableHeight() {
      return this.containerHeight / 2;
    },
    spreadTable0data() {
      return Array.isArray(this.spreadTable) &&
        this.spreadTable[0]?.data &&
        Array.isArray(this.spreadTable[0].data)
        ? this.spreadTable[0].data
        : [];
    },
    spreadTable1data() {
      return Array.isArray(this.spreadTable) &&
        this.spreadTable[1]?.data &&
        Array.isArray(this.spreadTable[1].data)
        ? this.spreadTable[1].data
        : [];
    },
    tableConversion() {
      return Array.isArray(this.spreadTable) && this.spreadTable[3]?.data
        ? this.spreadTable[3].data.conversion
        : 0;
    },
    tableRate() {
      return Array.isArray(this.spreadTable) && this.spreadTable[3]?.data
        ? this.spreadTable[3].data.rate
        : 0;
    },
    table1Len() {
      return Array.isArray(this.spreadTable) &&
        this.spreadTable[0]?.data &&
        Array.isArray(this.spreadTable[0].data)
        ? this.spreadTable[0].data.length
        : 0;
    },
    showLoading1() {
      return !(
        Array.isArray(this.spreadTable) &&
        this.spreadTable.length > 0 &&
        Array.isArray(this.spreadTable[0]?.data) &&
        this.spreadTable[0].data.length > 0
      );
    },
    showLoading2() {
      return !(
        Array.isArray(this.spreadTable) &&
        this.spreadTable.length > 1 &&
        Array.isArray(this.spreadTable[1]?.data) &&
        this.spreadTable[1].data.length > 0
      );
    },
    showLoading3() {
      return !(
        Array.isArray(this.spreadTable) &&
        this.spreadTable.length > 1 &&
        Array.isArray(this.spreadTable[2]?.data) &&
        this.spreadTable[2].data.length > 0
      );
    },
    formatCurrencyName() {
      return this.spreadTable[3]?.name?.[4] ?? "";
    },
    formatCommodity1() {
      return this.spreadTable[0]?.name ?? null;
    },
    formatCommodity2() {
      return this.spreadTable[1]?.name ?? null;
    },
    showConverted() {
      return (
        this.spreadTable[3]?.data?.conversion *
          this.spreadTable[3]?.data?.rate !=
        1
      );
    }
  },
  watch: {},
  methods: {
    onUserShowTAChart(idx, asset) {
      this.$emit("eventOnUserShowTAChart", {
        assetType: 1,
        irAvailableTerms: [{ label: "...", value: "..." }],
        selectedAsset: asset,
        selectedTerm: null,
        continuous_contract: idx,
        showFields: null,
        componentNumber: this.componentNumber
      });
    },
    updateHeight() {
      const el = this.$refs.componentContainer;
      if (el) {
        this.containerHeight = el.offsetHeight - 5;
      }
    },
    priceFormat(val) {
      return val === null || !isFinite(val) || val === 0
        ? "-"
        : Math.abs(val) > 10000
        ? format(",.0f")(val)
        : Math.abs(val) > 100
        ? format(",.2f")(val)
        : Math.abs(val) > 10
        ? format(",.3f")(val)
        : format(",.4f")(val);
    },
    changeFormat(val, price) {
      return val === null || !isFinite(val) || val === 0
        ? "-"
        : Math.abs(price) > 10000
        ? format(",.0f")(val)
        : Math.abs(price) > 100
        ? format(",.2f")(val)
        : Math.abs(price) > 10
        ? format(",.3f")(val)
        : format(",.4f")(val);
    },
    percentageFormat(val) {
      if (val == null || !isFinite(val) || val === 0) return "-";
      return format(",.2%")(val);
    },
    dateFormat(val) {
      if (!val) return "";
      const dateVal = new Date(val);
      const today = new Date();
      const sameDay = dateVal.toDateString() === today.toDateString();
      if (sameDay && dateVal.getHours() === 0 && dateVal.getMinutes() === 0) {
        return timeFormat("%d%b%y")(dateVal);
      }
      return sameDay
        ? timeFormat("%H:%M")(dateVal)
        : timeFormat("%d%b%y")(dateVal);
    },
    arrowClass(value) {
      if (value > 0) return "dayUp fa fa-play rotateUp";
      if (value < 0) return "dayDown fa fa-play rotateDown";
      return "";
    },
    formatSpreadName(index, idx1, idx2) {
      const name = this.spreadTable[index]?.name || "";
      return `(${name[idx1]}/${name[idx2]})`;
    }
  },
  filters: {
    priceFormat: function(val) {
      return val === null || !isFinite(val) || val === 0
        ? "-"
        : Math.abs(val) > 10000
        ? format(",.0f")(val)
        : Math.abs(val) > 100
        ? format(",.2f")(val)
        : Math.abs(val) > 10
        ? format(",.3f")(val)
        : format(",.4f")(val);
    },
    changeFormat: function(val, price) {
      return val === null || !isFinite(val) || val === 0
        ? "-"
        : Math.abs(price) > 10000
        ? format(",.0f")(val)
        : Math.abs(price) > 100
        ? format(",.2f")(val)
        : Math.abs(price) > 10
        ? format(",.3f")(val)
        : format(",.4f")(val);
    },
    numFormat: function(val) {
      return val === null || !isFinite(val) || val === 0
        ? "-"
        : format(",")(val);
    },
    percentageFormat: function(val) {
      return val === null || !isFinite(val) || val === 0
        ? "-"
        : format(",.2%")(val);
    },
    dateFormat: function(val) {
      if (
        val === null ||
        val === "" ||
        val === 0 ||
        +val < +new Date(1980, 1, 1)
      ) {
        return "";
      }

      const dateVal = new Date(val);
      const today = new Date();

      const sameDay =
        dateVal.getFullYear() === today.getFullYear() &&
        dateVal.getMonth() === today.getMonth() &&
        dateVal.getDate() === today.getDate();

      const isMidnight = dateVal.getHours() === 0 && dateVal.getMinutes() === 0;

      // Si es hoy y no es medianoche → hora:minuto, de lo contrario → día mes año
      return sameDay && !isMidnight
        ? timeFormat("%H:%M")(dateVal)
        : timeFormat("%d%b%y")(dateVal);
    }
  }
};
</script>
<style scoped>
.min_w {
  min-width: 60px;
}

.dayUp {
  color: var(--symmetry-h);
}
.rotateUp {
  transform: rotate(270deg) !important;
}

.dayDown {
  color: var(--red);
}
.rotateDown {
  transform: rotate(90deg) !important;
}

.row_up {
  background-color: var(--expo-alpha-3);
  transition: background-color 0.5s ease !important;
}

.row_down {
  background-color: var(--red-alpha-3);
  transition: background-color 0.5s ease !important;
}

.row_up {
  animation: pulse-up 1s ease-in-out;
}

.row_down {
  animation: pulse-down 1s ease-in-out;
}

@keyframes pulse-up {
  0%,
  100% {
    background-color: var(--expo-alpha-3);
  }

  50% {
    background-color: var(--expo-alpha-2);
  }
}

@keyframes pulse-down {
  0%,
  100% {
    background-color: var(--red-alpha-3);
  }

  50% {
    background-color: var(--red-alpha-2);
  }
}

.real_time_table {
  table-layout: fixed;
  width: 100%;
}
.real_time_table th,
.real_time_table td {
  width: auto;
  text-align: center;
}

.real_time_table td.row_up,
.real_time_table td.row_down {
  background-color: transparent !important;
}

.active .toggle__switch_g::after {
  left: 50%;
  border: 1px solid var(--expo);
  width: 50%;
  background: var(--expo);
  box-shadow: 0 0 1px var(--expo);
}
.toggle__button_g .toggle__switch_g::after,
.toggle__button_g .toggle__switch_g::before {
  content: "";
}
</style>
